<template>
  <div class="BundleReview">
    <template v-if="components.length <= 0">
      <p class="t4 BundleReview__Title">
        {{ store.terms.autobundle_review.title }}
      </p>
      <div class="BundleReview__EmptyNotice">
        <p class="t4">
          {{ store.terms.autobundle_review.empty.title }}
        </p>
        <p class="p4">
          {{ store.terms.autobundle_review.empty.description }}
        </p>
      </div>
    </template>
    <template v-else-if="bundleUnavailable">
      <p class="t4 BundleReview__Title">
        {{ store.terms.autobundle_review.title }}
      </p>
      <ul class="BundleReview__List BundleReview__List--OOS">
        <li class="BundleReview__ListItem">
          <div
            class="BundleReview__Wrapper BundleReview__Wrapper--OOS BundleReview__Wrapper--Unavailable"
          >
            <div class="BundleReview__Details BundleReview__Details--OOS">
              <p class="t4">
                {{ store.terms.autobundle_review.unavailable.main }}
              </p>
              <p class="p5">
                {{ store.terms.autobundle_review.unavailable.sub }}
              </p>
            </div>
          </div>
        </li>
      </ul>
    </template>
    <template v-else-if="!bundleUnavailable">
      <p class="t4 BundleReview__Title">
        {{ store.terms.autobundle_review.title }}
      </p>
      <p v-if="!editedSelection" class="p5 BundleReview__Description">
        <Icon
          ref="pencil-chat-bubble"
          icon="pencil-chat-bubble"
          :content="store.terms.autobundle_review.description"
        />
      </p>
      <ul class="BundleReview__List">
        <li
          v-for="(component, index) in components"
          :key="component"
          class="BundleReview__ListItem"
        >
          <div
            class="BundleReview__Wrapper"
            :class="{
              'BundleReview__Wrapper--OOS':
                component?.variantBySelectedOptions?.quantityAvailable <= 0,
            }"
          >
            <picture>
              <source media="(min-width:320px)" :srcset="imageSrc(component)" />
              <img
                lazy="lazy"
                :src="imageSrc(component)"
                :alt="image(component).altText"
              />
            </picture>
            <div class="BundleReview__Details">
              <p class="t4">
                {{ component.title }}
              </p>
              <ul>
                <li v-for="option in selectedVariant(component)" :key="option">
                  <p class="p5">{{ option.name }}: {{ option.value }}</p>
                </li>
                <li>
                  <Price
                    :item="item"
                    :variant="component?.variantBySelectedOptions"
                    :is-component="true"
                    :location="componentLocation"
                    :ready="store.promoReady"
                  />
                </li>
                <li v-if="shipsBy(component)">
                  <Backorder :date="shipsBy(component)" />
                </li>
              </ul>
            </div>
            <button
              role="button"
              class="p5 BundleReview__Edit"
              @click="editSelection(index)"
            >
              {{ store.terms.autobundle_review.edit }}
            </button>
          </div>
          <p
            v-if="component.variantBySelectedOptions.quantityAvailable <= 0"
            class="p5 BundleReview__OOS"
          >
            {{ store.terms.autobundle_review.component.oos }}
          </p>
          <button
            v-if="component.variantBySelectedOptions.quantityAvailable <= 0"
            role="button"
            class="p5 BundleReview__OOS--CTA"
            @click="editSelection(index)"
          >
            {{ store.terms.autobundle_review.component.edit }}
          </button>
        </li>
      </ul>
      <div class="ProductForm__PriceContainer">
        <Price
          :item="item"
          :variant="store.selected.variant"
          :promoted-option-groups="promotedOptionGroups"
          :components="components"
          :selections="selections"
          :location="location"
          :show-messaging="true"
          :ready="store.promoReady"
        />
      </div>
      <Notification
        :free-shipping-title="store.freeShippingTerms?.banner_title"
        :free-shipping-copy="store.freeShippingTerms?.banner_copy"
      />
    </template>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { deepClone } from '@src/js/utils/performance';
import { useProductStore } from './Composables/Store';

import Icon from '../Global/Icon.vue';
import Price from '../Global/Price.vue';
import Backorder from './Backorder.vue';
import Notification from './Notification.vue';

const store = useProductStore();
const components = computed(() => store.getSelectedComponents);
const bundleUnavailable = computed(() => store.selected.bundleUnavailable);
const editedSelection = ref(false);

const props = defineProps({
  item: {
    type: Object,
    required: false,
  },
  selections: { type: Array, default: [] },
  promotedOptionGroups: { type: Array, default: [] },
});
const componentLocation = computed(() => 'auto-bundle');
const location = computed(() => 'auto-bundle-sum');
const image = (component) => {
  const selection = component?.variantBySelectedOptions ?? false;
  const hasVariantFromSelections = Object.keys(selection).length > 0 ?? false;

  if (hasVariantFromSelections) return selection.image;
  else return component.variants[0].image;
};

const imageSrc = (component) => {
  const imageValue = image(component).url;
  const imageValueUrl =
    imageValue && imageValue.includes('768x.jpg')
      ? imageValue.replace('768x.jpg', '150x.jpg')
      : imageValue;
  return imageValueUrl;
};

const selectedVariant = (component) => {
  const selection = component?.variantBySelectedOptions ?? false;
  const hasVariantFromSelections = Object.keys(selection).length > 0 ?? false;

  if (hasVariantFromSelections) return selection.selectedOptions;

  return component.variants[0].selectedOptions;
};

const shipsBy = (component) => {
  return component.variantBySelectedOptions?.shipsBy?.value || false;
};
const editSelection = (index) => {
  editedSelection.value = true;

  store.$state.scrollPosition = window.scrollY;
  const components = store.$state.selected.components;
  const desiredComponent = deepClone(components[index]);
  const componentByIndex = store.$state.components[index];
  const payloadType = typeof componentByIndex;

  if (payloadType !== 'string') {
    desiredComponent.handle = componentByIndex[0];
  }
  // Store the index so handleEdit knows which component to update
  desiredComponent.editIndex = index;
  store.$state.edit = desiredComponent;
};
</script>
